/**
 * Общая модель локали для всех приложений compo (витрина, кабинет, админка).
 *
 * Список контент-локалей берётся из environment (`contentLocales`), а не из
 * хардкода: добавить язык = правка `environment.<стенд>.ts`
 * (`contentLocales: ['ru','en','kk']`), без правки кода. `setSupportedLocales(...)`
 * зовётся один раз на старте приложения (admin — `main.ts` ДО bootstrap; customer
 * и SSR — `APP_INITIALIZER`) и наполняет `SUPPORTED_LOCALES` IN-PLACE — ссылка
 * стабильна, поэтому все load-time захваты (`UI_LOCALES = SUPPORTED_LOCALES` и др.)
 * видят актуальный состав.
 *
 * Переводы ИНТЕРФЕЙСА от списка не зависят: `INTERFACE_DICTS`/`TUI_LANGUAGES`
 * (i18n-init) — `Partial`-мапы с fallback на ru, поэтому новая content-локаль без
 * перевода интерфейса (kk и т.п.) получает ru-интерфейс автоматически.
 */
export type AppLocale = string;

/** Локаль по умолчанию (канон): поведение «как без переводов». */
export const DEFAULT_LOCALE: AppLocale = 'ru';

/**
 * Все поддерживаемые контент-локали (lowercase), первой — дефолтная (канон).
 * Источник правды для UI и валидации. Наполняется из environment через
 * {@link setSupportedLocales}; до вызова — дефолт `['ru','en']` (= прежнее
 * захардкоженное поведение). Мутируется IN-PLACE — НЕ переприсваивать
 * (ссылку держат `UI_LOCALES` и прочие load-time потребители).
 */
export const SUPPORTED_LOCALES: string[] = ['ru', 'en'];

/**
 * Задаёт состав контент-локалей из environment (`contentLocales`). Нормализует
 * (trim + lowercase + дедуп), гарантирует присутствие {@link DEFAULT_LOCALE}
 * (канон — первым, как backend `source-locale`), пустой/невалидный вход → остаётся
 * только канон. Мутирует {@link SUPPORTED_LOCALES} IN-PLACE, чтобы ранее захваченные
 * ссылки (`UI_LOCALES`) увидели новый состав. Идемпотентна.
 */
export function setSupportedLocales(locales: readonly string[] | null | undefined): void {
  const out: string[] = [];
  const push = (raw: unknown): void => {
    if (typeof raw !== 'string') return;
    const tag = raw.trim().toLowerCase();
    if (tag && !out.includes(tag)) out.push(tag);
  };
  push(DEFAULT_LOCALE); // канон всегда присутствует и идёт первым
  for (const l of locales ?? []) {
    push(l);
  }
  SUPPORTED_LOCALES.length = 0;
  SUPPORTED_LOCALES.push(...out);
}

/**
 * Выбор значения из словаря `locale -> T` с фолбэком на дефолтную локаль.
 * Используется типизированными каталогами UI-строк.
 */
export function pickByLocale<T>(byLocale: Record<AppLocale, T>, locale: AppLocale): T {
  return byLocale[locale] ?? byLocale[DEFAULT_LOCALE];
}

/** Type-guard: входит ли строка в число поддерживаемых локалей. */
export function isSupportedLocale(value: string): value is AppLocale {
  return SUPPORTED_LOCALES.includes(value);
}
